import { Grid, Typography } from "@mui/material";
import _ from "lodash";
import { observer } from "mobx-react";
import moment from "moment";
import React, { FunctionComponent } from "react";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxCalendarPopup from "components/UI/Calendar/AcxCalendarPopup";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { Answer } from "models/Answer";
import { AnswerTypeVariations, AnswerTypeNames } from "models/AnswerType";
import Question, { QuestionType } from "models/Question";
import { Tag } from "models/Tag";
import theme from "Theme/AppTheme";
import ConvoNote from "./ConvoNote";
import { ConvoModuleUIModel } from "./ConvoModuleUIModel";
import ConvoClip from "./ConvoClip";
import ConvoMultiTagClipWrapper from "./ConvoMultiTagClipWrapper";
import AcxQuestionDescription from "components/UI/AcxQuestionDescription";
import AcxQuestionRubric from "components/UI/AcxQuestionRubric";
import ConvoAutofillIndicator from "./ConvoAutofillIndicator";

const selectCustomStyle = {
    menuPortal: (provided, state) => ({ ...provided, zIndex: 9999 }),
};

interface OwnProps {
    question: Question;
    moduleUIModel?: ConvoModuleUIModel;
    leftBorderColor?: string;
    isDisabled?: boolean;
    isAutofilled?: boolean;
    getAnswerForQuestion: (
        question: Question,
        conversationModuleId: string | undefined,
    ) => Answer | undefined;
    setAnswerForQuestion: (
        question: Question,
        tags: Tag[],
        answer: Answer | undefined,
        conversationModuleId?: string,
    ) => Answer;
    setFillInAnswerForQuestion: (
        question: Question,
        fillInAnswer: string,
        answer: Answer | undefined,
        conversationModuleId?: string,
    ) => Answer;
    showClips?: boolean;
}

type Props = OwnProps;

const ConvoTOFIResponseAnswer: FunctionComponent<Props> = observer((props) => {
    const answer: Answer | undefined = props.getAnswerForQuestion(
        props.question,
        props.moduleUIModel?.conversationModuleId,
    );

    let activeTags: Tag | Tag[] | undefined = answer?.activeTags;

    if (props.question.answerType.variation === AnswerTypeVariations.Single) {
        activeTags =
            (activeTags?.length ?? 0) > 0 ? activeTags?.[0] : undefined;
    }

    const showClipMultiClip: boolean =
        props.question.answerType.variation !== AnswerTypeVariations.Single &&
        (props.question.answerType.answerTypeName === "Tag Response" ||
            props.question.answerType.answerTypeName === "Scored Tag Response");

    const showNotesAndClips = !!answer && !!answer.isAnswered;

    return (
        <Grid
            id={`question-${props.question.id}`}
            item
            xs={12}
            sx={{
                backgroundColor: "unset",
                pl: props.question.parentId || props.leftBorderColor ? 2 : 0,
                borderLeft: props.leftBorderColor
                    ? `2px solid ${props.leftBorderColor}`
                    : "none",
                pt: 1,
            }}
            container
            direction={"column"}
            justifyContent={"flex-start"}
            alignItems="stretch"
        >
            <Grid
                item
                xs={12}
                container
                justifyContent={"flex-start"}
                alignItems={"center"}
                sx={{
                    pr: 3.5,
                    pl: 3,
                }}
            >
                <Grid item>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item>
                            <Typography
                                sx={{
                                    color: "#1F1F1F",
                                    fontSize: "14px",
                                    letterSpacing: 0,
                                    lineHeight: "20px",
                                    fontWeight: "bold",
                                }}
                            >
                                {props.question?.questionText}{" "}
                                {props.question.required &&
                                    !answer?.isAnswered && (
                                        <span
                                            style={{
                                                color: theme.palette.error.main,
                                            }}
                                        >
                                            *
                                        </span>
                                    )}
                                <ConvoAutofillIndicator
                                    isVisible={props.isAutofilled}
                                />
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                {!!props.question.rubric && (
                    <Grid item xs={12} pt={1}>
                        <AcxQuestionRubric urlString={props.question.rubric} />
                    </Grid>
                )}
                <Grid item xs={12}>
                    <AcxQuestionDescription
                        text={props.question.helperText ?? ""}
                        isModalForTablesVariant
                    />
                </Grid>
            </Grid>
            {props.question.type !== QuestionType.QuestionHeading && (
                <Grid
                    item
                    xs={12}
                    direction={"row"}
                    wrap={"nowrap"}
                    container
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    sx={{ pl: 3, pr: 3, mb: 1 }}
                >
                    <Grid item xs={12}>
                        <AnswerSelectorComponent
                            answer={answer}
                            activeTags={activeTags}
                            question={props.question}
                            leftBorderColor={props.leftBorderColor}
                            moduleUIModel={props.moduleUIModel}
                            isDisabled={props.isDisabled}
                            setAnswerForQuestion={props.setAnswerForQuestion}
                            getAnswerForQuestion={props.getAnswerForQuestion}
                            setFillInAnswerForQuestion={
                                props.setFillInAnswerForQuestion
                            }
                        />
                    </Grid>
                </Grid>
            )}
            {showNotesAndClips && (
                <>
                    <ConvoNote
                        question={props.question}
                        answer={answer}
                        columnDirection={"column-reverse"}
                        isDisabled={props.isDisabled}
                        conversationModuleId={
                            props.moduleUIModel?.conversationModuleId
                        }
                    />
                    {props.showClips &&
                        (showClipMultiClip ? (
                            <ConvoMultiTagClipWrapper
                                question={props.question}
                                answer={answer}
                                isDisabled={!answer}
                            />
                        ) : (
                            <ConvoClip
                                question={props.question}
                                answer={answer}
                                isDisabled={!answer}
                            />
                        ))}
                </>
            )}
        </Grid>
    );
});

const AnswerSelectorComponent = observer(
    (
        props: OwnProps & {
            children?: React.ReactNode;
            answer: Answer | undefined;
            activeTags: Tag | Tag[] | undefined;
        },
    ) => {
        const conversationModuleId = props.moduleUIModel?.conversationModuleId;

        function onFillInAnswerSelect(fillInAnswer: string) {
            props.setFillInAnswerForQuestion(
                props.question,
                fillInAnswer,
                props.answer,
                conversationModuleId,
            );
        }

        if (
            props.question.answerType.variation === AnswerTypeVariations.Single
        ) {
            function onSingleAnswerSelect(arg?: Tag) {
                props.setAnswerForQuestion(
                    props.question,
                    arg ? [arg] : [],
                    props.answer,
                    conversationModuleId,
                );
            }

            return (
                <AcxSelectSingle
                    options={
                        props.moduleUIModel?.getTagsFormattedForTagResponse(
                            props.question.id,
                        ) ?? []
                    }
                    containerHeight={"auto"}
                    customStyle={selectCustomStyle}
                    menuPlacement={"auto"}
                    menuPortalTarget={document.body}
                    id={`tag-response-${props.question.id}`}
                    fullWidth
                    colorField={
                        props.moduleUIModel?.moduleTypeName === "Interaction"
                            ? undefined
                            : "sentimentColor"
                    }
                    defaultValue={props.activeTags as any}
                    valueField={"id"}
                    labelField={"value"}
                    isClearable
                    onChange={onSingleAnswerSelect}
                    isDisabled={props.isDisabled}
                />
            );
        } else if (
            props.question.answerType.answerTypeName ===
            AnswerTypeNames.DateResponse
        ) {
            const currentDate = props.answer?.fillInAnswerValue
                ? moment(props.answer.fillInAnswerValue)
                : undefined;

            return (
                <AcxCalendarPopup
                    id={`fill-in-response-${props.question.id}`}
                    onSelect={(date) =>
                        onFillInAnswerSelect(date.format("YYYY-MM-DD"))
                    }
                    currentDate={currentDate}
                    disabled={props.isDisabled}
                />
            );
        } else if (
            props.question.answerType.answerTypeName ===
            AnswerTypeNames.TextResponse
        ) {
            const value = props.answer?.fillInAnswerValue
                ? props.answer.fillInAnswerValue
                : undefined;

            // eslint-disable-next-line react-hooks/exhaustive-deps
            const debounceOnChange = React.useCallback(
                // Short debounce keeps the autofill indicator responsive while still
                // throttling store writes (which recompute the whole question list).
                _.debounce((fillInValue) => {
                    onFillInAnswerSelect(fillInValue);
                }, 150),
                [],
            );

            return (
                <AcxMainTextField
                    id={`fill-in-response-${props.question.id}`}
                    onChange={(evt) => debounceOnChange(evt.target.value)}
                    multiline
                    rows={3}
                    value={value}
                    maxLength={props.question.maxLength}
                    isDisabled={props.isDisabled}
                    textContainerSx={{
                        paddingLeft: theme.spacing(1),
                        paddingRight: theme.spacing(1),
                        paddingBlock: theme.spacing(1),
                    }}
                />
            );
        } else {
            function onAnswerSelect(arg?: Tag[]) {
                props.setAnswerForQuestion(
                    props.question,
                    arg ?? [],
                    props.answer,
                    conversationModuleId,
                );
            }

            return (
                <AcxSelectMulti
                    options={
                        props.moduleUIModel?.getTagsFormattedForTagResponse(
                            props.question.id,
                        ) ?? []
                    }
                    inputLabel={"Add tag(s)"}
                    containerHeight={"auto"}
                    customStyle={selectCustomStyle}
                    menuPlacement={"auto"}
                    menuPortalTarget={document.body}
                    id={`tag-response-${props.question.id}`}
                    fullWidth
                    colorField={"sentimentColor"}
                    defaultValue={props.activeTags as any}
                    valueField={"id"}
                    labelField={"value"}
                    isClearable
                    onChange={onAnswerSelect}
                    isDisabled={props.isDisabled}
                />
            );
        }
    },
);

export default ConvoTOFIResponseAnswer;
